import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, sectionOf } from "@/lib/api";
import { MemberPhoto } from "./MemberPhoto";
import { SocialLinks } from "./SocialLinks";
import { BioButton } from "./BioButton";
import { Reveal, SectionLabel } from "./Reveal";

const LAST_ROW = { 1: "lg:col-span-12", 2: "lg:col-span-6", 3: "lg:col-span-4" };
const span = (i, n) => {
  if (i === 0) return "lg:col-span-6 lg:row-span-2";
  const r = Math.max(n - 5, 0) % 4;
  return i >= 5 && r && i >= n - r ? LAST_ROW[r] : "lg:col-span-3";
};

const MemberCard = ({ m, i, n }) => (
  <motion.div
    data-testid={`member-card-${i}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden bg-[#121216] h-[440px] sm:h-[380px] lg:h-auto ${span(i, n)}`}
  >
    <MemberPhoto
      src={m.image_url}
      alt={m.name}
      testId={`member-logo-card-${i}`}
      imgClass="grayscale group-hover:grayscale-0 transition-[filter] duration-700"
      glow="bg-[radial-gradient(circle_at_50%_35%,rgba(86,237,74,0.14),transparent_60%)]"
      logoClass={`absolute left-1/2 -translate-x-1/2 object-contain opacity-90 group-hover:scale-110 group-hover:rotate-[-4deg] transition-transform duration-700 ${i === 0 ? "top-[14%] w-1/2 max-w-[300px]" : "top-[10%] w-[34%] max-w-[130px]"}`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
    <span className="absolute top-4 left-4 font-mono text-xs tracking-[0.2em] text-[#56ED4A]">{String(i + 1).padStart(2, "0")}</span>
    <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">{m.role}</p>
      <h3 className={`font-display uppercase leading-[0.9] mt-1 ${i === 0 ? "text-5xl md:text-7xl" : "text-3xl md:text-4xl"}`}>{m.name}</h3>
      <div className="mt-4 flex flex-wrap gap-2"><BioButton m={m} testPrefix={`member-${i}`} /><SocialLinks links={m.links} testPrefix={`member-${i}`} /></div>
    </div>
  </motion.div>
);

export const Members = () => {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get("/members").then((r) => setMembers(Array.isArray(r.data) ? r.data.filter((m) => sectionOf(m) === "crew") : [])).catch(() => {});
  }, []);
  return (
    <section id="crew" data-testid="crew-section" className="px-5 md:px-10 py-24 md:py-36 bg-[#0d0d10]">
      <Reveal>
        <SectionLabel index="03">The crew</SectionLabel>
        <div className="mt-6 grid lg:grid-cols-2 gap-8 items-end">
          <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.85]">Link up <span className="stroke-text">with</span> the guys</h2>
          <p className="text-zinc-300 text-base md:text-lg max-w-md lg:justify-self-end">Every face behind the mics. Tap straight through to their pages — no middleman.</p>
        </div>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[360px] gap-3">
        {members.map((m, i) => <MemberCard key={m.id} m={m} i={i} n={members.length} />)}
      </div>
    </section>
  );
};
